import { AlertCircle, RefreshCw } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { DURATION_OPTIONS } from './constants';
import { formatDate, formatTime } from './utils';

/**
 * "Reschedule Booking" dialog (parent-initiated, NOT the drag-and-drop
 * variant — that's a separate AlertDialog).
 */
export default function RescheduleModal({
  open, onOpenChange,
  selectedBooking, rescheduleForm, setRescheduleForm,
  lanes, onConfirm,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="reschedule-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-blue-500" />
            Reschedule Booking
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {selectedBooking && (
            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              <p><strong>Current Booking:</strong></p>
              <p>{selectedBooking.lane_name} on {formatDate(selectedBooking.date)}</p>
              <p>{formatTime(selectedBooking.start_time)} - {formatTime(selectedBooking.end_time)}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label>New Lane</Label>
            <Select
              value={rescheduleForm.lane_id}
              onValueChange={(v) => setRescheduleForm({ ...rescheduleForm, lane_id: v })}
            >
              <SelectTrigger data-testid="reschedule-lane-select">
                <SelectValue placeholder="Select lane" />
              </SelectTrigger>
              <SelectContent>
                {lanes.map((lane) => (
                  <SelectItem key={lane.id} value={lane.id}>{lane.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>New Date</Label>
              <Input
                type="date"
                value={rescheduleForm.date}
                onChange={(e) => setRescheduleForm({ ...rescheduleForm, date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                data-testid="reschedule-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>New Start Time</Label>
              <Input
                type="time"
                value={rescheduleForm.start_time}
                onChange={(e) => setRescheduleForm({ ...rescheduleForm, start_time: e.target.value })}
                data-testid="reschedule-time-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Duration</Label>
            <Select
              value={rescheduleForm.duration}
              onValueChange={(v) => setRescheduleForm({ ...rescheduleForm, duration: v })}
            >
              <SelectTrigger data-testid="reschedule-duration-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="text-sm text-slate-500 bg-amber-50 p-3 rounded-lg">
            <p className="flex items-center gap-1">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              Rescheduling must be done at least 2 hours before your booking.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={onConfirm}
            className="bg-blue-500 hover:bg-blue-600"
            data-testid="confirm-reschedule-btn"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Confirm Reschedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
